import * as React from 'react'
import {ChangeEvent, ReactElement, RefObject} from 'react'
import {Moment} from 'moment/moment'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import NatDisasterRepo from '../../repository/NatDisasterRepo'
import Scroll from 'react-scroll'
import {NatPostDisasterCommentRequest} from '../../repository/NatPostDisasterComment'
import {NatPostDisasterCommentFileUploadRequest} from '../../repository/NatPostDisasterCommentFileUpload'
import {Nullable} from '../../../../../app/TypeHelpers'
import {InputError} from './NatDisasterCommentError'
import {Messages} from '../../../../../Messages'
import {NatPutDisasterCommentRequest} from '../../repository/NatPutDisasterComment'
import NatResidentDisasterCommentList from './NatResidentDisasterCommentList'
import {CommentInfo} from '../../repository/NatGetDisasterComment'
import {BlockSpacer, currentMedia} from '../../../../../styledComponents/ResponsiveConvenience'
import NatResidentDisasterNewComment from './NatResidentDisasterNewComment'
import {FloatingObserver} from '../NatDisasterTopicFloatingObserver'
import {Box} from '@mui/material'
import {PPTextWithClearForResident} from '../../../../../components/shared/forms/resident/PPTextWithClearForResident'
import {FlexContainer, RightRowFlexContainer} from '../../../../../styledComponents/FlexComponents'
import styled from 'styled-components'
import {DefaultStyledButton} from '../../../../../styledComponents/Button'
import {HKConstants} from '../../../../../HKConstants'
import AuthorityChecker from '../../../../../components/AuthorityChecker'
import {CookieHelper} from '../../../../../CookieHelper'
import {VIEW_DO_OR_NOT} from '../../../../../CodeMaster'
import {SearchIcon} from '../../../../common/icons/NatSvgIcon'

interface Props {
    pastFlag?: boolean,
    condominiumCd: string
    topicId: number
    currentDateTime: Moment
    comments: CommentInfo[]
    commentReadMap: Map<number, number>
    onPostSuccess: () => Promise<void>
    onReadComments: (commentIds: number[]) => Promise<void>
    disasterRepo: NatDisasterRepo
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType
}

interface State {
    inputKeyword: string
    searchKeyword: string
    searchCount: number
    searchResultComments: CommentInfo[]
    fileUploadError: Nullable<InputError>
}

export const KEY_DISASTER_COMMENT_NAME_PRIVATE: string = 'disasterCommentNamePrivate'

export default class NatDisasterCommentArea extends React.PureComponent<Props, State> {
    commentRef: RefObject<HTMLDivElement>
    newCommentRef: RefObject<HTMLDivElement>

    constructor(props: Readonly<Props>) {
        super(props)
        this.state = {
            inputKeyword: '',
            searchKeyword: '',
            searchCount: 0,
            searchResultComments: [],
            fileUploadError: null
        }

        this.commentRef = React.createRef()
        this.newCommentRef = React.createRef()
    }

    handleChangeSearchText = (event: ChangeEvent<HTMLInputElement>): void => {
        this.setState({inputKeyword: event.target.value})
    }

    handleCommentSearch = (): void => {
        this.setState({searchKeyword: this.state.inputKeyword}, async () => {
            await this.getViewCommentsList(this.props.comments, this.state.searchKeyword)
        })
    }

    handleSearchCansel = (): void => {
        this.setState({inputKeyword: '', searchKeyword: '', searchCount: 0, searchResultComments: []})
    }

    handleScroll = (element: RefObject<HTMLDivElement>): void => {
        if (element.current) {
            Scroll.animateScroll.scrollTo(element.current.offsetTop, {
                delay: 100,
                duration: 500,
                smooth: true
            })
        }
    }

    async createDisasterComment(request: NatPostDisasterCommentRequest): Promise<Nullable<number>> {
        const {disasterRepo, condominiumCd, topicId} = this.props

        const response = await disasterRepo.createDisasterComment(condominiumCd, topicId, request)
        if (response.requestError === null) {
            return response.details
        }
        return null
    }

    async updateDisasterComment(commentId: number, request: NatPutDisasterCommentRequest): Promise<Nullable<number>> {
        const {disasterRepo, condominiumCd, topicId} = this.props

        const response = await disasterRepo.updateDisasterComment(condominiumCd, topicId, commentId, request)
        if (response.requestError === null) {
            return commentId
        }
        return null
    }

    postCommentWithFile = async (
        actionType: 'reply' | 'new' | 'edit',
        action: () => Promise<Nullable<number>>,
        fileRequests: NatPostDisasterCommentFileUploadRequest[],
        posterNamePrivateFlag: string
    ): Promise<Nullable<InputError>> => {
        await this.props.eventEmitter.showLoading()
        let inputError: Nullable<InputError> = null
        CookieHelper.setStringValue(KEY_DISASTER_COMMENT_NAME_PRIVATE, posterNamePrivateFlag)
        try {
            const commentId: Nullable<number> =
                await this.props.asyncCallFunction(
                    action,
                    async (httpErrorOrNetworkError) => {
                        const messages = ['入力内容に誤りがあります。']
                        if (httpErrorOrNetworkError.responseBody) {
                            httpErrorOrNetworkError.responseBody.requestItemErrors.forEach(item => {
                                messages.push(item.message)
                            })
                        }

                        inputError = {
                            title: Messages.EOPCR0001.toString('コメント'),
                            messages: messages
                        }
                        await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    }
                )
            if (!commentId) {
                return inputError
            }
            this.handleClearFileUploadError()
            if (fileRequests.length > 0) {
                inputError = await this.fileUpload(fileRequests, commentId)
                if (inputError !== null) {
                    this.setState({fileUploadError: inputError})
                }
            }
            await this.props.onPostSuccess()
            switch (actionType) {
                case 'reply':
                    await this.getViewCommentsList(this.props.comments, this.state.searchKeyword)
                    break
                case 'new':
                    this.handleSearchCansel()
                    this.handleScroll(this.commentRef)
                    break
                case 'edit':
                    await this.getViewCommentsList(this.props.comments, this.state.searchKeyword)
                    break
                default:
                    break
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        return inputError
    }

    handleClearFileUploadError = (): void => {
        this.setState({fileUploadError: null})
    }

    handleReplyComment = async (
        commentRequest: NatPostDisasterCommentRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ): Promise<Nullable<InputError>> => {
        return await this.postCommentWithFile(
            'reply',
            () => this.createDisasterComment(commentRequest),
            fileRequests,
            commentRequest.posterNamePrivateFlag
        )
    }

    handleNewComment = async (
        commentRequest: NatPostDisasterCommentRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ): Promise<Nullable<InputError>> => {
        return await this.postCommentWithFile(
            'new',
            () => this.createDisasterComment(commentRequest),
            fileRequests,
            commentRequest.posterNamePrivateFlag
        )
    }

    handleEditComment = async (
        commentId: number,
        commentRequest: NatPutDisasterCommentRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ): Promise<Nullable<InputError>> => {
        return await this.postCommentWithFile(
            'edit',
            () => this.updateDisasterComment(commentId, commentRequest),
            fileRequests,
            commentRequest.posterNamePrivateFlag
        )
    }

    handleDeleteComment = async (commentId: number): Promise<void> => {
        const {disasterRepo, condominiumCd, topicId} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                disasterRepo.deleteDisasterComment.bind(
                    disasterRepo, condominiumCd, topicId, commentId),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )
            if (response && response.requestError === null) {
                await this.props.onPostSuccess()
                if (this.state.searchKeyword) {
                    await this.getViewCommentsList(this.props.comments, this.state.searchKeyword)
                }
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    fileUpload = async (
        requests: NatPostDisasterCommentFileUploadRequest[],
        commentId: number
    ): Promise<Nullable<InputError>> => {
        const {disasterRepo, condominiumCd, topicId} = this.props

        let reportIndex: number = 1
        const fileCount: number = requests.length
        const errorFiles: string[] = []

        for (const request of requests) {
            this.props.eventEmitter.changeLoadingMessage(`ファイルアップロード中... ${reportIndex}/${fileCount}`)
            try {
                // AsyncCallFunctionによる標準の通信エラー処理を行わない
                // 原因はなんであれファイルを再登録する必要があることをユーザーに伝える必要があるため、全ての例外を捕捉する
                await disasterRepo.fileUploadDisasterComment(condominiumCd, topicId, commentId, request)
                reportIndex = reportIndex + 1
            } catch (e) {
                errorFiles.push(request.fileName)
            }
        }

        if (errorFiles.length > 0) {
            const messages = ['アップロードの最中に問題が発生しました。\n' + '編集から再度アップロードしてください。']
            return {
                commentId: commentId,
                title: '画像のアップロードに失敗しました。',
                messages: messages,
                fileNameList: errorFiles
            }
        }
        return null
    }

    getViewCommentsList = async (comments: CommentInfo[], searchKeyword: string) => {
        if (!searchKeyword) {
            return comments
        }

        const counter =
            (text: Nullable<string>, exactMatching?: boolean) => {
                if (!text) {
                    return 0
                }

                if (exactMatching) {
                    return text === searchKeyword ? 1 : 0
                } else {
                    return text.split(searchKeyword).length - 1
                }
            }

        let searchCount: number = 0
        const searchResultComments = comments.filter(mainComment => {

            let threadCount: number = 0
            if (!mainComment.deleteDatetime) {
                threadCount = counter(mainComment.comment)
                    + counter(mainComment.posterRidgeName)
                    + counter(mainComment.posterRoomNumber)
                    + counter(mainComment.posterName, true)
            }

            mainComment.reply.forEach(replyComment => {
                if (!replyComment.deleteDatetime) {
                    threadCount += counter(replyComment.comment)
                        + counter(replyComment.posterRidgeName)
                        + counter(replyComment.posterRoomNumber)
                        + counter(replyComment.posterName, true)
                }
            })
            if (threadCount > 0) {
                searchCount += threadCount
                return true
            }
            return false
        })

        // 返信コメントのみ既読する（主コメントは初期表示時に既読しているので不要）
        const involvementReplyCommentIds = searchResultComments.flatMap(comment =>
            comment.reply
                .filter(reply => reply.deleteDatetime === null)
                .map(reply => reply.commentId))
        await this.props.onReadComments(involvementReplyCommentIds)

        this.setState({
            searchResultComments: searchResultComments,
            searchCount: searchCount
        })
    }

    renderSearchArea(): ReactElement {
        const {inputKeyword, searchKeyword, searchCount} = this.state

        return (
            <>
                <RightRowFlexContainer>
                    <SearchContainer>
                        <PPTextWithClearForResident
                            inputProps={{
                                name: 'inputKeyword',
                                value: inputKeyword,
                                placeholder: 'コメントを検索'
                            }}
                            icon={<SearchIcon/>}
                            onChange={this.handleChangeSearchText}
                            onCansel={this.handleSearchCansel}
                        />
                    </SearchContainer>
                    <FlexContainer>
                        <BlockSpacer width={5}/>
                    </FlexContainer>
                    <Box width={'64px'}>
                        <SearchButton onClick={this.handleCommentSearch}>
                            検索
                        </SearchButton>
                    </Box>
                </RightRowFlexContainer>
                {searchKeyword &&
                    <Box textAlign={'right'} pt={'5px'}>{`${searchCount}件`}</Box>
                }
            </>
        )
    }

    render(): ReactElement {
        const {pastFlag, condominiumCd, topicId, currentDateTime, comments, commentReadMap} = this.props
        const {searchKeyword, searchResultComments, fileUploadError} = this.state
        const defaultPosterNamePrivateFlag = CookieHelper.getStringValue(KEY_DISASTER_COMMENT_NAME_PRIVATE)
            === VIEW_DO_OR_NOT.HIDDEN.value

        return (
            <>
                <BlockSpacer height={'30px'}/>
                {this.renderSearchArea()}
                <BlockSpacer height={'15px'}/>
                <div ref={this.commentRef}>
                    <NatResidentDisasterCommentList
                        pastFlag={pastFlag}
                        condominiumCd={condominiumCd}
                        topicId={topicId}
                        comments={searchKeyword ? searchResultComments : comments}
                        commentReadMap={commentReadMap}
                        searchKeyword={searchKeyword}
                        currentDateTime={currentDateTime}
                        onReply={this.handleReplyComment}
                        onEdit={this.handleEditComment}
                        onDelete={this.handleDeleteComment}
                        onReadComments={this.props.onReadComments}
                        onClearFileUploadError={this.handleClearFileUploadError}
                        fileUploadError={fileUploadError}
                    />
                </div>
                {!pastFlag &&
                    <AuthorityChecker
                        permittedAuthorities={HKConstants.CMN.AUTH.POST_COMMENT_DISASTER_INFORMATION_SHARING}>
                        <BlockSpacer height={'15px'}/>
                        <div ref={this.newCommentRef}>
                            <NatResidentDisasterNewComment
                                condominiumCd={condominiumCd}
                                topicId={topicId}
                                posterNamePrivateFlag={defaultPosterNamePrivateFlag}
                                currentDateTime={currentDateTime}
                                onNewComment={this.handleNewComment}
                            />
                        </div>
                        <FloatingObserver
                            target={this.newCommentRef}
                            onClink={this.handleScroll}
                        />
                    </AuthorityChecker>
                }
            </>
        )
    }
}

const SearchButton = styled(DefaultStyledButton)`
    min-height: 34px;
`

const SearchContainer = styled.div`
    flex-basis: 276px;
    min-width: 0;
    ${currentMedia.lessThan('sp')`
      flex: 1
    `}
`